// Pure functions, no Angular DI — same convention as offline-cache-key.ts.
//
// A lesson note embeds a media-library video as <iframe src="/media?moduleid=…&uploadid=…">
// (Quill's MediaBlot; the URL is built server-side as MediaViewModel.playerurl). These helpers
// find those videos, so that saving a lesson can save them too and the offline reader can play
// them in place. Shared by both so the two can never disagree about which iframes are videos.

export interface NoteVideoRef {
  moduleid: number;
  uploadid: string;
  // `${moduleid}:${uploadid}` — the resourceId saved videos are stored under (media.component.ts).
  resourceId: string;
}

// The video an iframe src points at, or null if it is not one of this app's own /media frames
// (YouTube and other external embeds are left alone).
export function parseMediaFrameSrc(src: string | null | undefined): NoteVideoRef | null {
  if (!src) return null;
  let url: URL;
  try {
    url = new URL(src, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin || url.pathname !== '/media') return null;
  const moduleid = Number(url.searchParams.get('moduleid'));
  const uploadid = url.searchParams.get('uploadid');
  if (!Number.isFinite(moduleid) || moduleid <= 0 || !uploadid) return null;
  return { moduleid, uploadid, resourceId: `${moduleid}:${uploadid}` };
}

// Every distinct video embedded across a lesson's notes, in page order.
export function extractNoteVideoRefs(notes: Array<{ safeContent?: string; content?: string }> | null | undefined): NoteVideoRef[] {
  const found = new Map<string, NoteVideoRef>();
  for (const note of notes ?? []) {
    const html = note?.safeContent ?? note?.content ?? '';
    if (!html.includes('<iframe')) continue;
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    for (const frame of Array.from(doc.querySelectorAll('iframe'))) {
      const ref = parseMediaFrameSrc(frame.getAttribute('src'));
      if (ref && !found.has(ref.resourceId)) found.set(ref.resourceId, ref);
    }
  }
  return Array.from(found.values());
}
